import { inject, Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import { SelectModel } from 'src/shared/components/forms/select/select_model';
import { accountexternal, prodexternal, trialmanagementexternal } from 'src/shared/services/client/client';
import { ClientService } from 'src/shared/services/client/client.service';

export interface PhoneCodeOption extends SelectModel {
  emoji: string;
}

@Injectable({
  providedIn: 'root',
})
export class AccountDataService {
  private client = inject(ClientService);

  get phoneCodeOptions(): PhoneCodeOption[] {
    return [
      {
        name: 'Australia',
        value: 'AU',
        data: '+61',
        emoji: '🇦🇺',
      },
      {
        name: 'Belgium',
        value: 'BE',
        data: '+32',
        emoji: '🇧🇪',
      },
      {
        name: 'Brazil',
        value: 'BR',
        data: '+55',
        emoji: '🇧🇷',
      },
      {
        name: 'Canada',
        value: 'CA',
        data: '+1',
        emoji: '🇨🇦',
      },
      {
        name: 'China',
        value: 'CN',
        data: '+86',
        emoji: '🇨🇳',
      },
      {
        name: 'Czech Republic',
        value: 'CZ',
        data: '+420',
        emoji: '🇨🇿',
      },
      {
        name: 'Denmark',
        value: 'DK',
        data: '+45',
        emoji: '🇩🇰',
      },
      {
        name: 'Finland',
        value: 'FI',
        data: '+358',
        emoji: '🇫🇮',
      },
      {
        name: 'Germany',
        value: 'DE',
        data: '+49',
        emoji: '🇩🇪',
      },
      {
        name: 'Spain',
        value: 'ES',
        data: '+34',
        emoji: '🇪🇸',
      },
      {
        name: 'France',
        value: 'FR',
        data: '+33',
        emoji: '🇫🇷',
      },
      {
        name: 'Greece',
        value: 'GR',
        data: '+30',
        emoji: '🇬🇷',
      },
      {
        name: 'Hungary',
        value: 'HU',
        data: '+36',
        emoji: '🇭🇺',
      },
      {
        name: 'Ireland',
        value: 'IE',
        data: '+353',
        emoji: '🇮🇪',
      },
      {
        name: 'India',
        value: 'IN',
        data: '+91',
        emoji: '🇮🇳',
      },
      {
        name: 'Italy',
        value: 'IT',
        data: '+39',
        emoji: '🇮🇹',
      },
      {
        name: 'Japan',
        value: 'JP',
        data: '+81',
        emoji: '🇯🇵',
      },
      {
        name: 'South Korea',
        value: 'KR',
        data: '+82',
        emoji: '🇰🇷',
      },
      {
        name: 'Luxembourg',
        value: 'LU',
        data: '+352',
        emoji: '🇱🇺',
      },
      {
        name: 'Mexico',
        value: 'MX',
        data: '+52',
        emoji: '🇲🇽',
      },
      {
        name: 'Netherlands',
        value: 'NL',
        data: '+31',
        emoji: '🇳🇱',
      },
      {
        name: 'New Zealand',
        value: 'NZ',
        data: '+64',
        emoji: '🇳🇿',
      },
      {
        name: 'Norway',
        value: 'NO',
        data: '+47',
        emoji: '🇳🇴',
      },
      {
        name: 'Poland',
        value: 'PL',
        data: '+48',
        emoji: '🇵🇱',
      },
      {
        name: 'Portugal',
        value: 'PT',
        data: '+351',
        emoji: '🇵🇹',
      },
      {
        name: 'Russia',
        value: 'RU',
        data: '+7',
        emoji: '🇷🇺',
      },
      {
        name: 'Sweden',
        value: 'SE',
        data: '+46',
        emoji: '🇸🇪',
      },
      {
        name: 'Switzerland',
        value: 'CH',
        data: '+41',
        emoji: '🇨🇭',
      },
      {
        name: 'Turkey',
        value: 'TR',
        data: '+90',
        emoji: '🇹🇷',
      },
      {
        name: 'South Africa',
        value: 'ZA',
        data: '+27',
        emoji: '🇿🇦',
      },
      {
        name: 'UK',
        value: 'GB',
        data: '+44',
        emoji: '🇬🇧',
      },
      {
        name: 'USA',
        value: 'US',
        data: '+1',
        emoji: '🇺🇸',
      },
    ];
  }

  async GetUser(): Promise<accountexternal.GetUserResponse> {
    // @TODO: refactor usages to use Observables.
    const client = await this.client.getRequestClient();
    return await client.accountexternal.GetUser('my');
  }

  GetMyUser(): Observable<accountexternal.GetUserResponse> {
    return this.client.requestWithClient((client) => client.accountexternal.GetUser('me'));
  }

  DeleteMyUser(): Observable<void> {
    return this.client.requestWithClient((client) => client.accountexternal.DeleteUser('me'));
  }

  async UpsertUser(): Promise<accountexternal.UpsertSSOUserResponse> {
    const client = await this.client.getRequestClient();
    return await client.accountexternal.UpsertSSOUser({});
  }

  async CreateUser(dto: accountexternal.CreateUserRequest): Promise<accountexternal.CreateUserResponse> {
    const client = await this.client.getRequestClient();
    return await client.accountexternal.CreateUser(dto);
  }

  async UpdateUser(dto: accountexternal.UpdateUserRequest): Promise<accountexternal.UpdateUserResponse> {
    const client = await this.client.getRequestClient();
    return await client.accountexternal.UpdateUser('my', dto);
  }

  UndoAccountDeletion(currentUserId: string): Observable<void> {
    return this.client.requestWithClient((client) => client.accountexternal.CancelDeleteUser(currentUserId));
  }

  GetShopifyNavigationLink(
    props: prodexternal.GetShopifyNavigationLinkRequest
  ): Observable<prodexternal.GetShopifyNavigationLinkResponse> {
    return this.client.requestWithClient((client) => client.prodexternal.GetShopifyNavigationLink(props));
  }

  GetCurrentUser() {
    return this.client.client.pipe(switchMap((client) => client.accountexternal.GetUser('me')));
  }
  VerifyEmail() {
    return this.client.requestWithClient((client) => client.accountexternal.GenerateEmailVerificationLink());
  }
  SetTrialOnboardingConsent(
    userDocID: string,
    consent: trialmanagementexternal.SetTrialOnboardingConsentRequest
  ): Observable<void> {
    return this.client.requestWithClient((client) =>
      client.trialmanagementexternal.SetTrialOnboardingConsent(userDocID, consent)
    );
  }
}
